/**
 * #149/#154 — bloco proprio dos titulos PENDENTES com previsao de pagamento
 * JA passada (data_previsao < hoje).
 *
 * NIVEL FORA DA SERIE: o grafico do Fluxo so desenha dias >= hoje, entao esses
 * titulos nao aparecem em nenhuma barra — nem por vencimento (outra tela) nem por
 * previsao (esta). Sao invisiveis nas duas. Este bloco e o UNICO lugar onde
 * podem aparecer. Por isso: nao vai no tooltip (nao tem dia) nem no subtitulo
 * (nao e fato da serie inteira).
 *
 * Estados (doutrina #246, fallback nunca e mudo):
 *  - loading        -> "carregando"
 *  - error/!data    -> "indisponivel" (CP vivo do Sync fora), NUNCA zero
 *  - disponivel=false -> idem, com o motivo do backend
 *  - n=0            -> "nenhum titulo" (esse zero e MEDIDO, nao ausencia)
 *  - n>0            -> "N titulos · R$ X · mais antigo dd/mm/aaaa" + lista em expandir
 */
import type { ReactNode } from "react";
import { Info } from "lucide-react";
import type { ExistenciaSyncMarca, PrevisaoVencidaFonte, PrevisaoVencidaResponse } from "../../api/types";
import { fmtBRL } from "../../utils/formatters";

export interface PrevisaoVencidaCardProps {
  data: PrevisaoVencidaResponse | null;
  isLoading: boolean;
  error: Error | null;
}

const TITULO = "Títulos com previsão de pagamento vencida";
const AJUDA =
  "Títulos pendentes do CP vivo (Sync) cuja data de PREVISÃO de pagamento já passou. " +
  "Não entram em nenhuma barra do gráfico (que só desenha dias a partir de hoje) nem na " +
  "tela por vencimento. Previsão no passado não é defeito: pode ser título não pago e não " +
  "reprogramado — é informação para quem reprograma (Tati/Veiga), não conserto do Córtex.";

// 22/09/2026 (decisao do Veiga): titulo AUSENTE da listagem da fonte e SINALIZADO,
// nao purgado. Na janela by-id de 22/09, 51 titulos deste bloco voltaram
// "nao cadastrado" na Omie e as ultimas confirmacoes vao de 19/08 a 17/09, em 12
// dias distintos — um processo que remove ao longo de um mes, nao um apagao em
// bloco; purgar destruiria a evidencia. A marca diz o FATO e a DATA: nada de
// "inexistente"/"cancelado" (a fonte so diz que nao esta na listagem hoje) e nada
// que sugira acao. O dano que ela evita nao e caixa: e alguem perseguir um
// fornecedor por titulo que a fonte nao tem.
// TRAVA DO DENOMINADOR: sem marca NAO e confirmado — por isso o cabecalho mostra
// os TRES contadores (ausentes / confirmadas / nao avaliadas) e a data da listagem.
function _fonteTexto(f: PrevisaoVencidaFonte | null | undefined): { texto: string; classe: string } {
  if (!f) return { texto: "não avaliada", classe: "text-white/40" };
  if (f.estado === "ausente") {
    const dias = f.dias_sem_confirmacao !== null && f.dias_sem_confirmacao !== undefined ? `há ${f.dias_sem_confirmacao} d` : "";
    const ult = f.ultima_confirmacao ? `última confirmação ${_isoParaBR(f.ultima_confirmacao)}` : "sem confirmação registada";
    return { texto: `ausente na fonte ${dias} · ${ult}`.replace("  ", " "), classe: "text-amber-200/90" };
  }
  if (f.estado === "confirmada") {
    return { texto: `confirmada na fonte em ${_isoParaBR(f.ultima_confirmacao)}`, classe: "text-white/60" };
  }
  return { texto: "não avaliada", classe: "text-white/40" };
}

// 02/10/2026 (#264): a marca do SYNC ao lado da nossa. Duas reguas: a nossa e a
// listagem que o nosso sweep viu; a deles e a listagem deles + a consulta POR ID.
// So `nao_encontrado_by_id` e confirmacao da Omie; `ausente_n_ciclos` e ausencia.
// Sem marca do Sync NAO e confirmado — diz-se "sem marca".
function _syncTexto(m: ExistenciaSyncMarca | null | undefined): { texto: string; classe: string } {
  if (!m || m.estado === "nao_declarada") return { texto: "sem marca", classe: "text-white/40" };
  const dias = m.dias_ausente !== null && m.dias_ausente !== undefined ? ` há ${m.dias_ausente} d` : "";
  if (m.estado === "nao_encontrado_by_id") {
    return { texto: `Omie confirmou por id: não cadastrado${dias}`, classe: "text-[#FF6B6B]" };
  }
  if (m.estado === "ausente_n_ciclos") {
    return { texto: `ausente da listagem${dias}`, classe: "text-amber-200/90" };
  }
  if (m.estado === "confirmada") return { texto: "confirmada", classe: "text-white/60" };
  return { texto: `valor desconhecido: ${m.literal ?? "?"}`, classe: "text-amber-200/90" };
}

function _isoParaBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

export function PrevisaoVencidaCard({ data, isLoading, error }: PrevisaoVencidaCardProps) {
  const shell = (children: ReactNode, testid: string) => (
    <div
      className="bg-indra-azul-surface rounded-xl border border-white/10 p-4 mt-4"
      data-testid="previsao-vencida-card"
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h3 className="text-white font-semibold text-sm">{TITULO}</h3>
        <span className="cursor-help" title={AJUDA} data-testid="previsao-vencida-ajuda">
          <Info size={12} className="text-white/40" />
        </span>
        <span className="text-[10px] text-white/40 uppercase tracking-wider ml-auto">
          fora do gráfico · CP vivo (Sync) · previsão &lt; hoje
        </span>
      </div>
      <div data-testid={testid}>{children}</div>
    </div>
  );

  if (isLoading && !data) {
    return shell(<span className="text-white/50 text-xs">carregando…</span>, "previsao-vencida-loading");
  }
  if (error || !data || !data.disponivel) {
    return shell(
      <span
        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded text-amber-200/90 bg-amber-400/10 border border-amber-300/30"
        title={error?.message ?? data?.fonte ?? "CP vivo do Sync indisponível"}
      >
        indisponível — CP vivo do Sync não respondeu; a contagem NÃO é zero, é desconhecida
      </span>,
      "previsao-vencida-indisponivel",
    );
  }
  if (data.n === 0) {
    return shell(
      <span className="text-white/60 text-xs">
        nenhum título pendente com previsão passada (as of {_isoParaBR(data.as_of)})
      </span>,
      "previsao-vencida-vazio",
    );
  }

  const maisAntigo = data.itens.reduce<string | null>(
    (acc, i) => (acc === null || i.data_previsao < acc ? i.data_previsao : acc),
    null,
  );
  return shell(
    <>
      <div className="flex items-baseline gap-3 flex-wrap font-mono tabular-nums">
        <span className="text-[#FF6B6B] font-semibold text-base" data-testid="previsao-vencida-n">
          {data.n} {data.n === 1 ? "título" : "títulos"}
        </span>
        <span className="text-white/90 text-base" data-testid="previsao-vencida-valor">
          {fmtBRL(data.valor)}
        </span>
        <span className="text-white/50 text-[11px]" data-testid="previsao-vencida-mais-antigo">
          mais antigo: previsão {_isoParaBR(maisAntigo)}
        </span>
        <span className="text-white/40 text-[10px] ml-auto">as of {_isoParaBR(data.as_of)}</span>
      </div>
      {typeof data.fonte_ausentes_n === "number" && (
        <div className="mt-1 text-[11px] font-mono tabular-nums flex flex-wrap gap-x-3" data-testid="previsao-vencida-fonte">
          <span className="text-amber-200/90" data-testid="previsao-vencida-fonte-ausentes">
            {data.fonte_ausentes_n} de {data.n} sem confirmação na fonte
            {data.fonte_ausentes_valor ? ` · ${fmtBRL(data.fonte_ausentes_valor)}` : ""}
          </span>
          <span className="text-white/50">
            {data.fonte_confirmadas_n ?? 0} confirmadas
            {data.fonte_ultima_listagem ? ` (listagem ${_isoParaBR(data.fonte_ultima_listagem)})` : ""}
          </span>
          <span className="text-white/50">{data.fonte_nao_avaliadas_n ?? 0} não avaliadas</span>
          <span className="cursor-help text-white/40" title={data.fonte_regua ?? ""} data-testid="previsao-vencida-fonte-regua">
            sem marca ≠ confirmado: a confirmação vale para a data mostrada
          </span>
        </div>
      )}
      {typeof data.sync_confirmadas_n === "number" && (
        <div className="mt-1 text-[11px] font-mono tabular-nums flex flex-wrap gap-x-3" data-testid="previsao-vencida-sync">
          <span className="text-white/50">Sync:</span>
          <span className="text-[#FF6B6B]" data-testid="previsao-vencida-sync-nao-encontrados">
            {data.sync_nao_encontrados_n ?? 0} confirmados por id como não cadastrados
            {data.sync_nao_encontrados_valor ? ` · ${fmtBRL(data.sync_nao_encontrados_valor)}` : ""}
          </span>
          <span className="text-amber-200/90">
            {data.sync_ausentes_n ?? 0} ausentes da listagem
            {data.sync_ausentes_valor ? ` · ${fmtBRL(data.sync_ausentes_valor)}` : ""}
          </span>
          <span className="text-white/50">{data.sync_confirmadas_n} confirmadas</span>
          <span className="text-white/50">
            {(data.sync_nao_declaradas_n ?? 0) + (data.sync_desconhecidas_n ?? 0)} sem marca
          </span>
          {typeof data.reguas_divergem_n === "number" && (
            <span className="text-white/60" data-testid="previsao-vencida-reguas">
              réguas: {data.reguas_divergem_n} divergem · {data.reguas_nao_comparaveis_n ?? 0} não comparáveis
            </span>
          )}
        </div>
      )}
      <details className="mt-2 group">
        <summary
          className="cursor-pointer text-[11px] text-indra-turquesa/80 hover:text-indra-turquesa select-none"
          data-testid="previsao-vencida-expandir"
        >
          ver os {data.n} títulos
        </summary>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-[11px] font-mono tabular-nums" data-testid="previsao-vencida-lista">
            <thead className="text-white/50 text-left">
              <tr>
                <th className="pr-3 py-1 font-normal">credor</th>
                <th className="pr-3 py-1 font-normal">categoria</th>
                <th className="pr-3 py-1 font-normal">previsão</th>
                <th className="pr-3 py-1 font-normal text-right">dias</th>
                <th className="pr-3 py-1 font-normal">situação</th>
                <th className="pr-3 py-1 font-normal">fonte</th>
                <th className="pr-3 py-1 font-normal">Sync</th>
                <th className="py-1 font-normal text-right">valor</th>
              </tr>
            </thead>
            <tbody className="text-white/80">
              {data.itens.map((i) => (
                <tr key={`${i.id_omie ?? ""}-${i.data_previsao}`} className="border-t border-white/5">
                  <td className="pr-3 py-1 truncate max-w-[220px]" title={i.credor}>{i.credor}</td>
                  <td className="pr-3 py-1 truncate max-w-[180px]" title={i.categoria}>{i.categoria}</td>
                  <td className="pr-3 py-1">{_isoParaBR(i.data_previsao)}</td>
                  <td className="pr-3 py-1 text-right">{i.dias_previsao_passada}</td>
                  <td className="pr-3 py-1">{i.situacao ?? "—"}</td>
                  <td className={`pr-3 py-1 ${_fonteTexto(i.fonte).classe}`} data-testid="previsao-vencida-fonte-linha">
                    {_fonteTexto(i.fonte).texto}
                  </td>
                  <td className={`pr-3 py-1 ${_syncTexto(i.existencia_sync).classe}`} data-testid="previsao-vencida-sync-linha">
                    {_syncTexto(i.existencia_sync).texto}
                    {i.reguas_concordam === false ? " · diverge da nossa" : ""}
                  </td>
                  <td className="py-1 text-right">{fmtBRL(i.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>,
    "previsao-vencida-conteudo",
  );
}
